import { Fragment, useMemo, useState } from 'react';
import { useSuspenseQuery } from '@apollo/client/react';
import { ChevronDown, ArrowDownUp, ArrowUp, ArrowDown } from 'lucide-react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import dayjs from 'dayjs';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Card, CardHeader, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { DepositStatusEnum, AssetContentTypeEnum, SyncStatusEnum, AssetTypeEnum } from 'src/graphql/schema';
import { AssetsDocument } from 'src/graphql/actions/asset/assets.query';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PROVIDER_KEY_TO_NAME } from 'src/shared/constants';
import { formatBytes } from 'src/utils/formatBytes';
import { Badge } from 'src/components/ui/badge';
import { cn } from 'src/lib/utils';
import { AssetTypeIcon, depositStatusMap, syncStatusMap } from 'src/containers/Deposits';

type IAssetRow = {
  id: string;
  asset: {
    depositSource: string;
    provider?: string;
    type: AssetTypeEnum;
  };
  size: string;
  date: {
    latestDeposit: string;
    lastUpdateCheck: string;
  };
  depositStatus: DepositStatusEnum;
  syncStatus?: SyncStatusEnum | null;
};

const columnHelper = createColumnHelper<IAssetRow>();
const columns = [
  columnHelper.accessor('asset', {
    header: (context) => `Assets (${context.table.getRowModel().rows.length})`,
    cell: (props) => {
      const { depositSource, provider, type } = props.getValue();

      const providerName = provider ? PROVIDER_KEY_TO_NAME[provider as keyof typeof PROVIDER_KEY_TO_NAME] : '';

      return (
        <div className="flex items-center gap-2">
          <AssetTypeIcon type={type} provider={provider} />
          <div className="grow">
            <div className="truncate max-w-[126px]" title={depositSource}>
              {depositSource}
            </div>
            {providerName && <div className="text-xs text-muted-foreground">{providerName}</div>}
          </div>
        </div>
      );
    },
    sortingFn: (rowA, rowB) => {
      const valueA = rowA.original.asset.depositSource;
      const valueB = rowB.original.asset.depositSource;

      return valueA.localeCompare(valueB);
    },
  }),
  columnHelper.accessor('size', {
    header: 'Size',
    cell: (props) =>
      props.getValue() ? <div className="whitespace-nowrap">{formatBytes(Number(props.getValue()))}</div> : null,
    enableSorting: false,
  }),
  columnHelper.accessor('date', {
    header: 'Last deposit',
    cell: (props) => {
      const { latestDeposit, lastUpdateCheck } = props.getValue();

      if (!latestDeposit) return null;

      const latestDepositDate = dayjs(latestDeposit).format('MMM DD, YYYY');
      const isLastCheckOverWeekAgo = lastUpdateCheck
        ? dayjs(lastUpdateCheck).isBefore(dayjs().subtract(1, 'week'))
        : false;
      const lastUpdateCheckDate = isLastCheckOverWeekAgo
        ? dayjs(latestDeposit).format('MMM DD, YYYY')
        : dayjs(lastUpdateCheck).fromNow();

      return (
        <Fragment>
          <div className="text-sm font-medium">{latestDepositDate}</div>
          {lastUpdateCheckDate && (
            <div className="text-[10px] text-muted-foreground whitespace-nowrap">Checked {lastUpdateCheckDate}</div>
          )}
        </Fragment>
      );
    },
    sortingFn: (rowA, rowB) => {
      const valueA = rowA.original.date.latestDeposit ? dayjs(rowA.original.date.latestDeposit).unix() : 0;
      const valueB = rowB.original.date.latestDeposit ? dayjs(rowB.original.date.latestDeposit).unix() : 0;

      return valueA - valueB;
    },
  }),
  columnHelper.accessor('syncStatus', {
    header: 'Sync status',
    cell: (props) => {
      const status = props.getValue();

      if (!status) return 'N/A';

      const syncStatus = syncStatusMap[status];

      if (!syncStatus) return 'N/A';

      const { label, textColor, dotColor } = syncStatus;

      return (
        <Badge variant="ghost" className={cn('gap-1 px-0', textColor)}>
          <span className={cn('rounded-full size-1.5', dotColor)} />
          {label}
        </Badge>
      );
    },
    sortingFn: 'text',
  }),
  columnHelper.accessor('depositStatus', {
    header: 'Deposit status',
    cell: (props) => {
      const { label, variant } = depositStatusMap[props.getValue()];

      return <Badge variant={variant}>{label}</Badge>;
    },
    sortingFn: 'text',
  }),
];

type Props = {
  title: string;
  description: string;
  assetsIds: string[];
  contentType: AssetContentTypeEnum | 'unlabeled';
};

const AssetsSuggestedChanges = ({ title, description, assetsIds, contentType }: Props) => {
  const {
    softwareEscrow: { id: softwareEscrowId },
  } = useSoftwareEscrow();
  const [isOpen, setIsOpen] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([]);
  const {
    data: { assets: totalAssets },
  } = useSuspenseQuery(AssetsDocument, {
    variables: {
      softwareEscrowId,
      perPage: 1,
      labeled: contentType !== 'unlabeled',
      ...(contentType !== 'unlabeled' && { contentType }),
    },
  });
  const {
    data: { assets },
  } = useSuspenseQuery(AssetsDocument, {
    variables: {
      ids: assetsIds,
      perPage: assetsIds.length,
      labeled: contentType !== 'unlabeled',
      ...(contentType !== 'unlabeled' && { contentType }),
    },
  });

  const tableData = useMemo(
    () =>
      assets.nodes.map((node) => ({
        id: node.id,
        asset: {
          depositSource: node.depositSource || '',
          provider: node.integration?.provider || '',
          type: node.type,
        },
        size: node.size || '',
        date: {
          latestDeposit: node.latestDeposit || '',
          lastUpdateCheck: node.lastUpdateCheck || '',
        },
        depositStatus: node.depositStatus || '',
        syncStatus: node.syncStatus || undefined,
      })),
    [assets.nodes],
  );
  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  if (!tableData.length) return null;

  const infoItems = [
    {
      label: 'Total number of assets',
      value: totalAssets.nodesCount,
    },
    {
      label: 'Removal requests',
      value: assets.nodesCount,
      className: 'bg-accent',
    },
  ];

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} asChild>
      <Card>
        <CardHeader className="flex-row justify-between items-center gap-2 space-y-0 [&:has(+[data-state=open])]:pb-0">
          <div className="text-base font-semibold">{title}</div>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="text-xs">
              {isOpen ? 'Hide' : 'Show'} assets
              <ChevronDown className={cn('transition-transform rotate-0', isOpen && 'rotate-180')} />
            </Button>
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent className="pb-0">
          <CardContent className="space-y-6">
            <CardDescription>{description}</CardDescription>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {infoItems.map(({ label, value, className }) => (
                <div
                  key={label}
                  className={cn('bg-background-secondary border border-border rounded-lg p-5 space-y-1', className)}
                >
                  <div className="text-lg font-bold text-foreground">{value}</div>
                  <div className="text-xs text-muted-foreground">{label}</div>
                </div>
              ))}
            </div>
            <div className="border border-border rounded-md">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead
                          key={header.id}
                          className={cn(
                            'bg-gray-50 text-xs text-muted-foreground font-bold whitespace-nowrap first:rounded-tl-md last:rounded-tr-md',
                            header.column.columnDef.meta?.headerClassName,
                          )}
                        >
                          {header.column.getCanSort() ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={header.column.getToggleSortingHandler()}
                              className="-ml-3 text-muted-foreground! text-xs! font-bold"
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {{
                                asc: <ArrowUp />,
                                desc: <ArrowDown />,
                              }[header.column.getIsSorted() as string] ?? <ArrowDownUp />}
                            </Button>
                          ) : (
                            flexRender(header.column.columnDef.header, header.getContext())
                          )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default AssetsSuggestedChanges;
